/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import WaitUntilAbstract, { WaitUntilPathType } from 'cio/app-models/action-abstracts/wait-until';
import { tracked } from '@glimmer/tracking';
import { ConditionType } from 'cio/utils/condition-types';
import {
  CAMPAIGN_TYPES_WITH_EVENT_TIME_SUPPORT,
  WAIT_UNTIL_ACTION_SUPPORTED_CONDITION_TYPES,
} from 'cio/utils/actions/wait-until';
import AuthService from 'cio/services/auth';
import { inject as service } from '@ember/service';

interface WaitUntilEditSidebarArgs {
  actionAbstract: WaitUntilAbstract;
  isSaving: boolean;
  onSelect: () => unknown;
  onDeselect: () => unknown;
  hasReadOnlyPermission: boolean;
}

type AddConditionDropdownItem = {
  label: string;
  value: string;
  type: WaitUntilPathType;
  disabled: boolean;
  conditionType?: ConditionType;
};

const MAX_PATH_COUNT = 20;

export default class WaitUntilEditSidebar extends Component<WaitUntilEditSidebarArgs> {
  @service auth!: AuthService;

  get addConditionItems() {
    return [
      {
        label: 'Segment',
        value: ConditionType.SegmentsNegatable,
        type: WaitUntilPathType.Conditions,
        conditionType: ConditionType.SegmentsNegatable,
        disabled: false,
      },
      {
        label: 'Attribute',
        value: ConditionType.Attribute,
        type: WaitUntilPathType.Conditions,
        conditionType: ConditionType.Attribute,
        disabled: this.auth.redacted,
      },
      {
        label: 'Event',
        value: ConditionType.Event,
        type: WaitUntilPathType.Conditions,
        conditionType: ConditionType.Event,
        disabled: false,
      },
      {
        label: 'Message',
        value: ConditionType.Message,
        type: WaitUntilPathType.Conditions,
        conditionType: ConditionType.Message,
        disabled: false,
      },
    ];
  }

  @action
  onAddConditionItem(value: AddConditionDropdownItem) {
    if (!this.canAddMorePaths) {
      return;
    }
    this.args.actionAbstract.addPath(value);
  }

  get isConditionsCardDisabled() {
    return this.args.actionAbstract.conditionPaths.length > 1;
  }

  get isEventTimeCardDisabled() {
    return !this.canAddMorePaths && !this.args.actionAbstract.hasEventDelayPath;
  }

  get isMaxTimeCardDisabled() {
    return !this.canAddMorePaths && !this.args.actionAbstract.hasMaximumWaitPath;
  }

  get canAddEventTimeCondition() {
    let { campaign } = this.args.actionAbstract.action;
    if (campaign == null) {
      return false;
    }
    return CAMPAIGN_TYPES_WITH_EVENT_TIME_SUPPORT.has(campaign.type);
  }

  get showConditionsSection() {
    return this.args.actionAbstract.paths.length > 0 || this.showConditionsDropdown;
  }

  MAX_PATH_COUNT = MAX_PATH_COUNT;

  get isPreBranchWaitUntil() {
    return this.args.actionAbstract.action.isPreBranchWaitUntil;
  }

  get canAddMorePaths() {
    return this.args.actionAbstract.paths.length < MAX_PATH_COUNT;
  }

  waitUntilConditionTypes = WAIT_UNTIL_ACTION_SUPPORTED_CONDITION_TYPES;

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked _showConditionsDropdown = this.args.actionAbstract.hasConditionPaths;
  get showConditionsDropdown() {
    if (this.isPreBranchWaitUntil) {
      return this._showConditionsDropdown && this.args.actionAbstract.conditionPaths.length < 1;
    }

    return this._showConditionsDropdown;
  }

  @action
  toggleConditions() {
    if (this._showConditionsDropdown) {
      // Don't allow toggling if more than one condition is present - they need
      // to be removed individually
      if (this.args.actionAbstract.conditionPaths.length > 1) {
        return;
      }

      // If there's only one condition, it can be removed
      if (this.args.actionAbstract.conditionPaths.length === 1) {
        this.args.actionAbstract.deleteConditionPath(0);
      }
    } else {
      if (this.isPreBranchWaitUntil) {
        this.args.actionAbstract.addPath({ type: WaitUntilPathType.Conditions });
      }
    }

    this._showConditionsDropdown = !this._showConditionsDropdown;
  }

  @action
  deleteConditionPath(index: number) {
    this.args.actionAbstract.deleteConditionPath(index);

    if (this.isPreBranchWaitUntil) {
      this._showConditionsDropdown = false;
    }
  }

  @action
  toggleEventAttributeTimeCondition() {
    if (this.args.actionAbstract.hasEventDelayPath) {
      this.args.actionAbstract.deleteEventDelayPath();
    } else if (this.canAddMorePaths) {
      this.shouldScrollPathSection = true;
      this.args.actionAbstract.addPath({ type: WaitUntilPathType.EventDelay });
    }
  }

  @action
  toggleMaximumWaitTime() {
    if (this.args.actionAbstract.hasMaximumWaitPath) {
      this.args.actionAbstract.deleteMaximumWaitPath();
    } else if (this.canAddMorePaths) {
      this.shouldScrollPathSection = true;
      this.args.actionAbstract.addPath({ type: WaitUntilPathType.MaxWait });
    }
  }

  shouldScrollPathSection = false;

  @action
  scrollPathSectionIntoView(element: HTMLElement) {
    // When inserting a new event or maximum wait time condition, we want to
    // scroll to it if there are too many conditions above it. However, since
    // we're calling this action from a did-insert modifier, we need to prevent
    // it from scrolling when first opening the edit sidebar - so we use
    // shouldScrollPathSection to track whether we should scroll.
    if (!this.shouldScrollPathSection) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.shouldScrollPathSection = false;
  }
}
